import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { createSelector } from "reselect";
import { toast } from "react-toastify";
import { Autocomplete, TextField } from "@mui/material";
import {
  Accordion,
  Button,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import LoadingSpinner from "components/LoadingSpinner";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import CancelBackButton from "Common/Components/Buttons/CancelBackButton";
import { MecordColors } from "Constants/Color";
import {
  fetchInwardFormData,
  processAccessoryInward,
} from "slices/Warehouse/ItemInward/ReturnedAccessories/thunk";
import { clearInwardFormData } from "slices/Warehouse/ItemInward/ReturnedAccessories/reducer";
import { showConfirm, showWarning } from "helpers/swalHelper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import FileUpload from "Common/Components/FileUpload.tsx/FileUpload";

// ─── types ───────────────────────────────────────────────────────────────────

interface GrnProduct {
  inventory_product_id: string;
  product_id: string;
  qc: "QC Pass" | "QC Fail";
  serial_no: string;
  warranty_date: string;
  remarks: string;
}

interface SerialOption {
  inventory_product_id: string;
  serial_no: string;
  warranty_date?: string | null;
}

interface AccessoryState {
  accessory_id: string | null;
  product_id: string | null;
  product_name: string;
  product_sku: string;
  issued_qty: number;
  return_qty: number;
  inwarded_qty: number;
  pending_qty: number;
  issued_inventory_product_ids: string[];
  serial_options: SerialOption[];  // returned unit serial options
  received_qty: number;
  good_qty: number;
  damaged_qty: number;
  grn_products: GrnProduct[];
}

interface BatchItemState {
  sales_order_service_item_id: string;
  product_name: string;
  product_sku: string;
  accessories: AccessoryState[];
}

interface BatchState {
  return_id: string;
  returned_at: string;
  note: string | null;
  status: string;
  items: BatchItemState[];
}

// ─── Add Stock view (full-page swap, exactly like existing GRN) ───────────────

interface AddStockViewProps {
  acc: AccessoryState;
  receivedDate: string;
  onClose: () => void;
  onSave: (grn_products: GrnProduct[]) => void;
}

const AddStockView: React.FC<AddStockViewProps> = ({
  acc,
  receivedDate,
  onClose,
  onSave,
}) => {
  const totalRows = acc.received_qty;
  const goodTarget = acc.good_qty;
  const damageTarget = acc.damaged_qty;

  const [rows, setRows] = useState<GrnProduct[]>(() => {
    if (acc.grn_products.length === totalRows) return acc.grn_products;
    return Array.from({ length: totalRows }, (_, i) => {
      const invId = acc.issued_inventory_product_ids[i] || "";
      const opt = (acc.serial_options || []).find(
        (o) => o.inventory_product_id === invId,
      );
      return {
        inventory_product_id: invId,
        product_id: acc.product_id || "",
        qc: (i < goodTarget ? "QC Pass" : "QC Fail") as "QC Pass" | "QC Fail",
        serial_no: opt?.serial_no || "",
        warranty_date: opt?.warranty_date
          ? String(opt.warranty_date).slice(0, 10)
          : "",
        remarks: "",
      };
    });
  });

  const [errors, setErrors] = useState<
    { serial_no?: string; warranty_date?: string }[]
  >(Array.from({ length: totalRows }, () => ({})));

  const passCount = rows.filter((r) => r.qc === "QC Pass").length;
  const failCount = rows.filter((r) => r.qc === "QC Fail").length;

  const handleChange = (idx: number, field: keyof GrnProduct, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setErrors((prev) => {
      const next = [...prev];
      if (next[idx]) next[idx][field as "serial_no" | "warranty_date"] = undefined;
      return next;
    });
  };

  const validate = () => {
    let ok = true;
    const errs = rows.map((r) => {
      const e: { serial_no?: string; warranty_date?: string } = {};
      if (!r.serial_no?.trim()) { e.serial_no = "Serial number is required"; ok = false; }
      if (!r.warranty_date) { e.warranty_date = "Warranty date is required"; ok = false; }
      return e;
    });
    setErrors(errs);
    return ok;
  };

  const handleSave = () => {
    if (passCount !== goodTarget || failCount !== damageTarget) {
      showWarning({
        title: "QC Mismatch",
        text: `Pass must be ${goodTarget} and Fail must be ${damageTarget}`,
      });
      return;
    }
    if (!validate()) {
      toast.error("Please fill all required fields");
      return;
    }
    onSave(rows);
  };

  return (
    <>
      <style>{`
        .stock-table { font-family: "Poppins", sans-serif; font-size: 13px; }
        .stock-table thead th { background-color: ${MecordColors.LightGrayColor}; color: ${MecordColors.NormalText}; font-weight: 500; text-align: center; }
        .stock-table tbody tr:hover { background-color: ${MecordColors.LightGrayColorHover}; }
        .stock-table td, .stock-table th { vertical-align: middle; text-align: center; }
      `}</style>

      {/* Summary row */}
      <div className="mb-4 mt-3" style={{ overflowX: "auto" }}>
        <Table className="table align-middle">
          <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
            <tr>
              <th style={{ minWidth: 60 }}>Sr. No</th>
              <th style={{ minWidth: 180 }}>Accessory</th>
              <th style={{ minWidth: 120 }}>Pending Qty</th>
              <th style={{ minWidth: 130 }}>Received Qty</th>
              <th style={{ minWidth: 120 }}>Good Qty</th>
              <th style={{ minWidth: 130 }}>Damaged Qty</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>{acc.product_name}</td>
              <td>{acc.pending_qty}</td>
              <td>{acc.received_qty}</td>
              <td>{acc.good_qty}</td>
              <td>{acc.damaged_qty}</td>
            </tr>
          </tbody>
        </Table>
      </div>

      <hr />

      {/* Per-serial QC table */}
      <div className="mb-4" style={{ overflowX: "auto" }}>
        <Table className="stock-table" bordered>
          <thead>
            <tr>
              <th style={{ minWidth: 60 }}>Sr No.</th>
              <th style={{ minWidth: 160 }}>Accessory</th>
              <th style={{ minWidth: 170 }}>Warranty Date</th>
              <th style={{ minWidth: 180 }}>Serial No.</th>
              <th style={{ minWidth: 100 }}>QC</th>
              <th style={{ minWidth: 180 }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{acc.product_name}</td>
                <td>
                  <Form.Control
                    type="date"
                    value={row.warranty_date}
                    min={receivedDate || undefined}
                    isInvalid={!!errors[i]?.warranty_date}
                    onChange={(e) => handleChange(i, "warranty_date", e.target.value)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[i]?.warranty_date}
                  </Form.Control.Feedback>
                </td>
                <td>
                  {acc.serial_options && acc.serial_options.length > 0 ? (
                    <>
                      <Autocomplete
                        size="small"
                        options={acc.serial_options}
                        getOptionLabel={(opt) => opt.serial_no || ""}
                        isOptionEqualToValue={(a, b) => a.inventory_product_id === b.inventory_product_id}
                        value={acc.serial_options.find((o) => o.inventory_product_id === row.inventory_product_id && o.serial_no === row.serial_no) || null}
                        onChange={(_e, selected) => {
                          if (!selected) {
                            setRows((prev) => {
                              const next = [...prev];
                              next[i] = {
                                ...next[i],
                                inventory_product_id: "",
                                serial_no: "",
                                warranty_date: "",
                              };
                              return next;
                            });
                            return;
                          }
                          // Update inventory id, serial, and warranty from inventory
                          setRows((prev) => {
                            const next = [...prev];
                            next[i] = {
                              ...next[i],
                              inventory_product_id: selected.inventory_product_id,
                              serial_no: selected.serial_no,
                              warranty_date: selected.warranty_date
                                ? String(selected.warranty_date).slice(0, 10)
                                : "",
                            };
                            return next;
                          });
                          setErrors((prev) => {
                            const next = [...prev];
                            if (next[i]) {
                              next[i].serial_no = undefined;
                              if (selected.warranty_date) next[i].warranty_date = undefined;
                            }
                            return next;
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select serial"
                            error={!!errors[i]?.serial_no}
                            helperText={errors[i]?.serial_no}
                            sx={{
                              "& .MuiInputBase-root": { fontSize: "0.82rem", backgroundColor: "#fff" },
                            }}
                          />
                        )}
                      />
                    </>
                  ) : (
                    <>
                      <Form.Control
                        type="text"
                        value={row.serial_no}
                        placeholder="Enter Serial No"
                        isInvalid={!!errors[i]?.serial_no}
                        onChange={(e) => handleChange(i, "serial_no", e.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors[i]?.serial_no}
                      </Form.Control.Feedback>
                    </>
                  )}
                </td>
                <td>
                  <span className={row.qc === "QC Pass" ? "text-success fw-bold" : "text-danger fw-bold"}>
                    {row.qc}
                  </span>
                </td>
                <td>
                  <Form.Control
                    type="text"
                    value={row.remarks}
                    onChange={(e) => handleChange(i, "remarks", e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
        <CancelBackButton onClick={onClose} label="Back" />
        <Button
          className="btn btn-sm"
          style={{
            backgroundColor: MecordColors.PrimaryButtonBGColor,
            color: MecordColors.PrimaryButtonTextColor,
            border: "none",
            padding: "8px 24px",
          }}
          onClick={handleSave}
        >
          Save Stock
        </Button>
      </div>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ProcessReturnedAccessories: React.FC = () => {
  document.title = "Process Returned Accessories";

  const { challan_id } = useParams<{ challan_id: string }>();
  const [searchParams] = useSearchParams();
  const return_id = searchParams.get("return_id") || undefined;
  
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const selector = createSelector(
    (state: any) => state.returnedAccessoriesInward,
    (s) => ({
      formData: s?.inwardFormData,
      loading: !!s?.inwardFormLoading,
      processLoading: !!s?.processLoading,
    }),
  );
  const { formData, loading, processLoading } = useSelector(selector);

  const [batches, setBatches] = useState<BatchState[]>([]);
  const [receivedDate, setReceivedDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [receivedBy, setReceivedBy] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [attachments, setAttachments] = useState<(File | string)[]>([]);

  const [addStockTarget, setAddStockTarget] = useState<{
    batchIdx: number;
    itemIdx: number;
    accIdx: number;
  } | null>(null);

  useEffect(() => {
    if (challan_id) dispatch(fetchInwardFormData({ challanId: challan_id, returnId: return_id }));
    return () => { dispatch(clearInwardFormData()); };
  }, [challan_id, return_id, dispatch]);

  useEffect(() => {
    if (!formData?.pending_batches) return;
    const initial: BatchState[] = (formData.pending_batches as any[]).map(
      (b: any) => ({
        return_id: b.return_id,
        returned_at: b.returned_at,
        note: b.note || null,
        status: b.status,
        items: (Array.isArray(b.items) ? b.items : []).map((item: any) => ({
          sales_order_service_item_id: item.sales_order_service_item_id,
          product_name: item.product_name,
          product_sku: item.product_sku,
          accessories: (Array.isArray(item.accessories) ? item.accessories : []).map(
            (acc: any) => ({
              accessory_id: acc.accessory_id || null,
              product_id: acc.product_id || null,
              product_name: acc.product_name,
              product_sku: acc.product_sku,
              issued_qty: Number(acc.issued_qty || 0),
              return_qty: Number(acc.return_qty || 0),
              inwarded_qty: Number(acc.inwarded_qty || 0),
              pending_qty: Number(acc.pending_qty || 0),
              issued_inventory_product_ids: Array.isArray(acc.issued_inventory_product_ids)
                ? acc.issued_inventory_product_ids
                : [],
              serial_options: Array.isArray(acc.serial_options)
                ? acc.serial_options
                : [],
              received_qty: Number(acc.pending_qty || 0),
              good_qty: Number(acc.pending_qty || 0),
              damaged_qty: 0,
              grn_products: [],
            }),
          ),
        })),
      }),
    );
    setBatches(initial);
  }, [formData]);

  const updateAcc = useCallback(
    (batchIdx: number, itemIdx: number, accIdx: number, update: Partial<AccessoryState>) => {
      setBatches((prev) =>
        prev.map((b, bi) =>
          bi !== batchIdx ? b : {
            ...b,
            items: b.items.map((it, ii) =>
              ii !== itemIdx ? it : {
                ...it,
                accessories: it.accessories.map((ac, ai) =>
                  ai !== accIdx ? ac : { ...ac, ...update },
                ),
              },
            ),
          },
        ),
      );
    },
    [],
  );

  const handleQtyChange = (
    batchIdx: number,
    itemIdx: number,
    accIdx: number,
    field: "received_qty" | "good_qty" | "damaged_qty",
    rawValue: string,
  ) => {
    const num = Math.max(0, parseInt(rawValue, 10) || 0);
    const acc = batches[batchIdx].items[itemIdx].accessories[accIdx];

    if (field === "received_qty") {
      if (num > acc.pending_qty) {
        showWarning({ title: "Invalid Quantity", text: "Received Quantity cannot exceed Pending Quantity." });
        return;
      }
      const newGood = Math.min(acc.good_qty, num);
      updateAcc(batchIdx, itemIdx, accIdx, { received_qty: num, good_qty: newGood, damaged_qty: num - newGood, grn_products: [] });
    } else if (field === "good_qty") {
      if (num > acc.received_qty) {
        showWarning({ title: "Invalid Quantity", text: "Good Quantity cannot exceed Received Quantity." });
        return;
      }
      updateAcc(batchIdx, itemIdx, accIdx, { good_qty: num, damaged_qty: acc.received_qty - num, grn_products: [] });
    } else if (field === "damaged_qty") {
      if (num > acc.received_qty) {
        showWarning({ title: "Invalid Quantity", text: "Damaged Quantity cannot exceed Received Quantity." });
        return;
      }
      updateAcc(batchIdx, itemIdx, accIdx, { damaged_qty: num, good_qty: acc.received_qty - num, grn_products: [] });
    }
  };

  const getAddStockAcc = () => {
    if (!addStockTarget) return null;
    const { batchIdx, itemIdx, accIdx } = addStockTarget;
    return batches[batchIdx]?.items[itemIdx]?.accessories[accIdx] || null;
  };

  const handleSaveGrnProducts = (grnProducts: GrnProduct[]) => {
    if (!addStockTarget) return;
    const { batchIdx, itemIdx, accIdx } = addStockTarget;
    updateAcc(batchIdx, itemIdx, accIdx, { grn_products: grnProducts });
    setAddStockTarget(null);
  };

  const validateBatch = (batch: BatchState): string | null => {
    for (const item of batch.items) {
      for (const acc of item.accessories) {
        if (acc.good_qty + acc.damaged_qty !== acc.received_qty) {
          return `Good + Damaged must equal Received for "${acc.product_name}"`;
        }
        if (acc.received_qty > acc.pending_qty) {
          return `Received (${acc.received_qty}) exceeds Pending (${acc.pending_qty}) for "${acc.product_name}"`;
        }
        if (acc.received_qty > 0 && acc.grn_products.length !== acc.received_qty) {
          return `Please click "Add Stock" and fill serial/QC details for "${acc.product_name}"`;
        }
      }
    }
    return null;
  };


  const handleSubmitAll = async () => {
    if (!receivedDate) { toast.error("Please select Received Date"); return; }
    if (!receivedBy.trim()) { toast.error("Please enter Received By"); return; }
    for (const batch of batches) {
      const err = validateBatch(batch);
      if (err) { showWarning({ title: "Validation Error", text: err }); return; }
    }
    const confirmed = await showConfirm(
      "Process inward for all pending return batches? GRN numbers will be generated.",
      "Confirm Inward",
      "Yes, Process",
    );
    if (!confirmed.isConfirmed) return;
    for (const batch of batches) {
      const itemsPayload = batch.items
        .map((item) => {
          const validAccessories = item.accessories.filter((acc) => acc.received_qty > 0);
          return {
            sales_order_service_item_id: item.sales_order_service_item_id,
            accessories: validAccessories.map((acc) => ({
              accessory_id: acc.accessory_id,
              product_id: acc.product_id,
              received_qty: acc.received_qty,
              good_qty: acc.good_qty,
              damaged_qty: acc.damaged_qty,
              grn_products: acc.grn_products,
            })),
          };
        })
        .filter((item) => item.accessories.length > 0);

      const formData = new FormData();
      formData.append("received_date", receivedDate);
      formData.append("received_by", receivedBy);
      if (note) formData.append("note", note);
      formData.append("items", JSON.stringify(itemsPayload));

      if (attachments?.length) {
        attachments.forEach((file: any) => {
          formData.append("attachments", file);
        });
      }

      const res = await dispatch(
        processAccessoryInward({ challanId: challan_id!, returnId: batch.return_id, formData }),
      );
      if (processAccessoryInward.fulfilled.match(res)) {
        const ragrn = res.payload?.data?.ragrn_no || res.payload?.ragrn_no;
        toast.success(`Inward processed! GRN: ${ragrn}`);
      } else {
        toast.error((res.payload as any)?.message || "Failed to process inward.");
        return;
      }
    }
    sessionStorage.setItem("WarehouseItemInwardTab", "2");
    navigate("/warehouse-item-inward");
  };

  const isAddStockOpen = !!addStockTarget;
  const addStockAcc = getAddStockAcc();

  return (
    <div
      className="page-content px-4"
      style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
    >
      <style>{`
        .common-table { font-family: "Poppins", sans-serif; font-size: 13px; }
        .common-table thead th { background-color: ${MecordColors.LightGrayColor}; color: ${MecordColors.NormalText}; font-weight: 500; }
        .common-table tbody tr:hover { background-color: ${MecordColors.LightGrayColorHover}; }
        .common-table td, .common-table th { vertical-align: middle; }
        .accordion-notes .accordion-button { background-color: ${MecordColors.LightGrayColor} !important; color: ${MecordColors.NormalText} !important; }
        .accordion-notes .accordion-button:not(.collapsed) { background-color: ${MecordColors.LightGrayColor} !important; color: ${MecordColors.NormalText} !important; }
      `}</style>

      <Container
        fluid
        style={{
          backgroundColor: "#fff",
          padding: "30px",
          borderRadius: "10px",
          boxShadow: "0 0 15px rgba(0,0,0,0.1)",
        }}
      >
        {/* Breadcrumb */}
        {!isAddStockOpen ? (
          <>
            <PageBreadcrumb
              title="Process Returned Accessories"
              titleLink="/warehouse-item-inward"
              breadcrumbs={[
                { label: "Item Inward", to: "/warehouse-item-inward" },
                { label: "Process Returned Accessories", to: "" },
              ]}
              backNavigationPath={-1}
            />
            <hr />
          </>
        ) : (
          <>
            <PageBreadcrumb
              title="Add Stock"
              breadcrumbs={[
                { label: "Process Returned Accessories", to: "" },
                { label: "Add Stock", to: "" },
              ]}
              isBackArrow={false}
            />
            <hr />
          </>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : isAddStockOpen && addStockAcc ? (
          <AddStockView
            acc={addStockAcc}
            receivedDate={receivedDate}
            onClose={() => setAddStockTarget(null)}
            onSave={handleSaveGrnProducts}
          />
        ) : (
          <>
            {/* ── Challan Details + Inward Details ── */}
            <Row className="mb-4 g-4">
              {/* LEFT – Challan Details */}
              <Col lg={6} md={12}>
                <h5 className="fw-semibold mb-3">Challan Details</h5>
                <div
                  className="p-3 rounded mb-3"
                  style={{ backgroundColor: MecordColors.LightGrayColor }}
                >
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Challan No.</Form.Label>
                        <Form.Control type="text" readOnly value={formData?.challan_no || ""} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Challan Date</Form.Label>
                        <Form.Control
                          type="date"
                          readOnly
                          value={formData?.challan_date ? formData.challan_date.split("T")[0] : ""}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sales Order No.</Form.Label>
                        <Form.Control type="text" readOnly value={formData?.sales_order_number || ""} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Warehouse</Form.Label>
                        <Form.Control type="text" readOnly value={formData?.warehouse_name || ""} />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* RIGHT – Inward Details */}
              <Col lg={6} md={12}>
                <h5 className="fw-semibold mb-3">Inward Details</h5>
                <div
                  className="p-3 rounded mb-3"
                  style={{ backgroundColor: MecordColors.ActiveBackground }}
                >
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          Received Date <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={receivedDate}
                          onChange={(e) => setReceivedDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          Received By <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter Name"
                          value={receivedBy}
                          onChange={(e) => setReceivedBy(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            <hr />

            {/* ── Return Batches ── */}
            {batches.length === 0 ? (
              <div className="text-center py-5 text-muted">
                No pending return batches found for this challan.
              </div>
            ) : (
              batches.map((batch, batchIdx) => (
                <div key={batch.return_id} className="mb-5">
                  {/* Batch header */}
                  <div className="mb-3">
                    <h5 className="fw-semibold mb-0">
                      Return Batch #{batchIdx + 1}
                    </h5>
                    {batch.returned_at && (
                      <small className="text-muted">
                        Initiated: <FormattedDate date={batch.returned_at} />
                      </small>
                    )}
                    {batch.note && (
                      <div>
                        <small className="text-secondary">Note: {batch.note}</small>
                      </div>
                    )}
                  </div>

                  {batch.items.map((item, itemIdx) => (
                    <div key={item.sales_order_service_item_id} className="mb-4">
                      <h6 className="fw-semibold mb-2">
                        {item.product_name}
                        {item.product_sku && (
                          <span className="text-muted fw-normal ms-2 small">
                            ({item.product_sku})
                          </span>
                        )}
                      </h6>

                      <div style={{ overflowX: "auto" }}>
                        <Table className="table align-middle common-table">
                          <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                            <tr>
                              <th style={{ minWidth: 40 }}>Sr.</th>
                              <th style={{ minWidth: 180 }}>Accessory</th>
                              <th style={{ minWidth: 80, textAlign: "center" }}>Issued</th>
                              <th style={{ minWidth: 100, textAlign: "center" }}>Return Req.</th>
                              <th style={{ minWidth: 100, textAlign: "center" }}>Already In.</th>
                              <th style={{ minWidth: 80, textAlign: "center" }}>Pending</th>
                              <th style={{ minWidth: 170, textAlign: "center" }}>Received Qty</th>
                              <th style={{ minWidth: 160, textAlign: "center" }}>Good Qty</th>
                              <th style={{ minWidth: 140, textAlign: "center" }}>Damaged Qty</th>
                              <th
                                style={{
                                  minWidth: 130,
                                  textAlign: "center",
                                  position: "sticky",
                                  right: 0,
                                  background: MecordColors.LightGrayColor,
                                  zIndex: 2,
                                }}
                              >
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.accessories.map((acc, accIdx) => {
                              const stockFilled =
                                acc.received_qty > 0 &&
                                acc.grn_products.length === acc.received_qty;
                              return (
                                <tr key={`${acc.accessory_id || acc.product_id}-${accIdx}`}>
                                  <td>{accIdx + 1}</td>
                                  <td>
                                    <div className="fw-semibold">{acc.product_name}</div>
                                    {acc.product_sku && (
                                      <small className="text-muted">{acc.product_sku}</small>
                                    )}
                                  </td>
                                  <td style={{ textAlign: "center" }}>{acc.issued_qty}</td>
                                  <td style={{ textAlign: "center" }}>{acc.return_qty}</td>
                                  <td style={{ textAlign: "center" }}>{acc.inwarded_qty}</td>
                                  <td style={{ textAlign: "center" }}>
                                    <span className={acc.pending_qty > 0 ? "text-warning fw-bold" : "text-success fw-bold"}>
                                      {acc.pending_qty}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    <Form.Control
                                      type="number"
                                      placeholder="Received Qty"
                                      value={acc.received_qty}
                                      min={0}
                                      max={acc.pending_qty}
                                      onChange={(e) => handleQtyChange(batchIdx, itemIdx, accIdx, "received_qty", e.target.value)}
                                      style={{ textAlign: "center" }}
                                    />
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    <Form.Control
                                      type="number"
                                      placeholder="Good Qty"
                                      value={acc.good_qty}
                                      min={0}
                                      max={acc.received_qty}
                                      disabled={!acc.received_qty}
                                      onChange={(e) => handleQtyChange(batchIdx, itemIdx, accIdx, "good_qty", e.target.value)}
                                      style={{ textAlign: "center" }}
                                    />
                                  </td>
                                  <td style={{ textAlign: "center" }}>{acc.damaged_qty}</td>
                                  <td
                                    style={{
                                      textAlign: "center",
                                      position: "sticky",
                                      right: 0,
                                      background: "white",
                                      zIndex: 2,
                                    }}
                                  >
                                    <Button
                                      className="btn btn-sm"
                                      disabled={!acc.received_qty}
                                      onClick={() => {
                                        if (!receivedDate) {
                                          showWarning({ title: "Missing Received Date", text: "Select Received Date before adding stock." });
                                          return;
                                        }
                                        if (acc.good_qty + acc.damaged_qty !== acc.received_qty) {
                                          showWarning({ title: "Validation Error", text: "Good + Damaged must equal Received Qty." });
                                          return;
                                        }
                                        setAddStockTarget({ batchIdx, itemIdx, accIdx });
                                      }}
                                      style={{
                                        backgroundColor: stockFilled ? "#28a745" : MecordColors.PrimaryButtonBGColor,
                                        color: MecordColors.PrimaryButtonTextColor,
                                        border: "none",
                                        padding: "6px 14px",
                                      }}
                                    >
                                      {stockFilled ? "✓ Done" : "Add Stock"}
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  ))}

                  <hr />
                </div>
              ))
            )}

            {/* Notes */}
            <Row className="mb-3 g-4">
              <h5 className="fw-semibold">Notes</h5>
              <Col md={12}>
                <Accordion defaultActiveKey="0" className="mb-3 accordion-notes">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Notes</Accordion.Header>
                    <Accordion.Body className="p-0">
                      <Form.Group className="mb-0">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          placeholder="Enter notes"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>

            <hr />

            {/* Attach Files */}
            <Row className="mb-3 g-4 mb-3">
              <FileUpload
                label={"Attachments"}
                multiple={true}
                value={attachments}
                onChange={(files: any) => setAttachments(files)}
              />
            </Row>

            {/* Action buttons */}
            {batches.length > 0 && (
              <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
                <CancelButton redirectTo="/warehouse-item-inward" disabled={processLoading} />
                <Button
                  disabled={processLoading}
                  onClick={handleSubmitAll}
                  style={{
                    backgroundColor: MecordColors.PrimaryButtonBGColor,
                    color: MecordColors.PrimaryButtonTextColor,
                    border: "none",
                    padding: "8px 24px",
                    opacity: processLoading ? 0.7 : 1,
                  }}
                >
                  {processLoading ? "Processing…" : "Submit Inward"}
                </Button>
              </div>
            )}

          </>
        )}
      </Container>
    </div>
  );
};

export default ProcessReturnedAccessories;
